<template>
  <BaseLayout
    v-if="isDefaultLayout"
    @logout="logout"
  />
  <YourNewScoreModal />
  <AlertDetailDrawer />
  <ReportInsightsDetailDrawer />
  <img
    :src="pixelSrc"
    aria-hidden="true"
    alt="pixel"
    class="mse-visitor-pixel"
    style="position: absolute; top: 0; right: 0; width: 1px; height: 1px"
  />
</template>

<script setup lang="ts">
  import { computed, onMounted, ref, watch } from 'vue';
  import { useRoute } from 'vue-router';

  import AlertDetailDrawer from '@/components/alert-detail-drawer/AlertDetailDrawer.vue';
  import YourNewScoreModal from '@/components/modals/YourNewScoreModal.vue';
  import ReportInsightsDetailDrawer from '@/components/report-insights-detail-drawer/ReportInsightsDetailDrawer.vue';
  import { useCreditProfile } from '@/hooks/useCreditProfile.js';
  import useLogout from '@/hooks/useLogout';
  import { useAlertsStore } from '@/stores';
  import { LOG_PREFIX, PORTAL_TYPE } from '@/types/enums';
  import BaseLayout from '@/views/layouts/BaseLayout.vue';

  const route = useRoute();
  const alertsStore = useAlertsStore();
  useCreditProfile();

  onMounted(async () => {
    window.$log.info(`[${LOG_PREFIX.APP}]: Credit club app mounted`);
  });

  const pixelSrc = ref('');

  const { handleLogout } = useLogout();

  const getPixelSrc = () => {
    let addr = 'https://webapi.moneysavingexpert.com/gb/pageImpression/v1.png';

    if (route.name === 'EligibilityLoansSearchPage' || route.name === 'EligibilityLoansResults') {
      addr += '?channel=336';
    } else if (
      route.name === 'EligibilityCreditCardsSearch' ||
      route.matched[0].name === 'EligibilityCreditCardsResults'
    ) {
      addr += '?channel=335';
    } else {
      addr += '?channel=334';
    }

    addr += '&pageClassification=mseCreditClub';
    addr += `&pageId=${encodeURIComponent(route.path)}`;
    addr += `&pageVersion=${encodeURIComponent('1.0')}`;
    addr += `&url=${encodeURIComponent(window.location.href)}`;
    addr += `&referrer=${encodeURIComponent(document.referrer)}`;
    addr += `&r=${Math.random() * 100000}`;

    return addr;
  };

  watch(route, () => {
    pixelSrc.value = getPixelSrc();
  });

  watch(
    () => route.meta.portalType,
    async (portalType) => {
      if (
        portalType !== PORTAL_TYPE.PORTAL ||
        alertsStore.alerts !== null ||
        alertsStore.isLoading
      ) {
        return;
      }

      await alertsStore.fetchAlerts();
    },
    { immediate: true },
  );

  const isDefaultLayout = computed<boolean>(() => !!route.meta.portalType);

  const logout = async () => {
    await handleLogout();
    location.replace('/creditclub/');
  };
</script>

<style lang="scss">
  @use '@mony-group/mint-themes/variables/mse.css' as *;
  @use '@/styles/main';
</style>
